import React, { useEffect, useState, useRef } from 'react';
import { clsx } from 'clsx';

// @ts-ignore
import shopifyHelper from '../../../../js/shopify';

declare global {
  interface Window {
    ProductTile?: any;
    ProductTiles?: any[];
    shopify?: any;
  }
}

interface SwatchItem {
  id?: string;
  title?: string;
  url?: string;
  image?: string;
  color?: string;
  colour?: string;
  name?: string;
  handle?: string;
  isSelected?: boolean;
  variantId?: string;
}

import { ProductSellingGroups } from './ProductSellingGroups/ProductSellingGroups';
import { ProductTags } from './ProductTags/ProductTags';
import { ProductRatings } from './ProductRatings/ProductRatings';
import { ProductPrice } from './ProductPrice/ProductPrice';
import { ProductImages } from './ProductImages/ProductImages';
import { ProductSwatches } from './ProductSwatches/ProductSwatches';
import QuickAdd from './ProductTileQuickAdd/ProductTileQuickAdd';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';
import { ProductWishlist } from './ProductWishlist/ProductWishlist';
import { ProductTitle } from './ProductTitle/ProductTitle';
import { ProductVendor } from './ProductVendor/ProductVendor';
import WishlistButton from '@src/stories/blocks/Wishlist/WishlistButton';


export const ProductTile = ({
  product,
  swatches = [],
  siblings,
  show_add = false,
  show_price_inline = false,
  settings,
  globals,
  enableWishlist,
  showPrice = true,
  showTags = true,
  showQuickAdd = true,
  manualLayout = null,
  type = 'default',
  isClosestMatch = false
}) => {
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [hasRetrievedMetafields, sethasRetrievedMetafields] = useState(false);
  const [colourName, setColourName] = useState([]);
  const [currentProduct, setCurrentProduct] = useState(product);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const productTileRef = useRef<HTMLDivElement>(null);

  const { isMobile } = useResponsiveBreakpoints();

  // Generate unique ID for this ProductTile instance
  const productTileId = useRef(
    `product-tile-${currentProduct?.id || 'unknown'}-${Math.random().toString(36).substr(2, 9)}`
  ).current;

  // Find the appropriate tile configuration
  const findTileConfig = () => {
    if (!window?.ProductTiles || window.ProductTiles.length === 0) {
      return { layout: [], type: 'default' };
    }

    // If a specific type is provided, find matching tile config
    if (type && type !== 'default') {
      const matchingTile = window.ProductTiles.find((tile) => tile.type === type);
      if (matchingTile) {
        return { layout: matchingTile.layout || [], type: matchingTile.type };
      }
    }

    // Default to first tile configuration
    const defaultTile = window.ProductTiles[0];

    return { layout: defaultTile.layout || [], type: defaultTile.type || 'default' };
  };

  const tileConfig = findTileConfig();
  const layout = tileConfig.layout;
  const tileType = tileConfig.type;

  // Function to fetch product data by handle
  const fetchProductByHandle = async (handle: string) => {
    try {
      setIsLoadingProduct(true);

      // Use Shopify.js productByHandle query
      if (handle) {
        const productData = await shopifyHelper.getProductByHandle(handle);
        if (productData && productData.id) {
          return {
            ...productData,
            handle: productData.handle,
            url: `/products/${productData.handle}`,
            variants: productData.variants || [],
            images: productData.images || [],
            featured_image: productData.featured_image,
            metafields: productData.metafields || {}
          };
        }
      }

      // If Shopify.js is not available or fails
      console.error(`Could not fetch product data for handle: ${handle} - Shopify.js not available or failed`);
      return null;
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    } finally {
      setIsLoadingProduct(false);
    }
  };

  // Handle product change events from swatches
  const handleProductChange = async (event: CustomEvent) => {
    const { productHandle, swatch } = event.detail;

    if (productHandle && productHandle !== currentProduct?.handle) {
      const newProductData = await fetchProductByHandle(productHandle);

      if (newProductData) {
        setCurrentProduct(newProductData);

        // Dispatch event to notify other components of the product change
        const productUpdatedEvent = new CustomEvent('product:updated', {
          detail: {
            oldProduct: currentProduct,
            newProduct: newProductData,
            swatch
          },
          bubbles: true
        });
        document.dispatchEvent(productUpdatedEvent);
      }
    }
  };

  // Utility function to inject CSS into document head
  const injectCSS = (css: string, id: string) => {
    // Remove existing style element if it exists
    // const existingStyle = document.getElementById(id);
    // if (existingStyle) {
    //   existingStyle.remove();
    // }

    // Only inject if CSS is not empty
    if (css.trim()) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = css;
      document.head.appendChild(style);
    }
  };

  // Function to parse min-heights string like cls-min-heights.liquid
  const parseMinHeightsString = (minHeightsString: string, productTileId: string) => {
    if (!minHeightsString) return;

    // Use a global style ID for all product tiles to consolidate CSS
    const styleId = `product-tile-parsed-heights-global--${tileType}`;

    // Get existing CSS from the style tag if it exists
    const existingStyle = document.getElementById(styleId);
    let existingCSS = existingStyle ? existingStyle.textContent || '' : '';

    // Split by newlines to get individual breakpoint:height pairs
    const minHeightsPairs = minHeightsString.split('\n').filter((line) => line.trim());

    // Create a Set to track unique media queries (normalized for comparison)
    const existingQueries = new Set();

    // Parse existing CSS to extract media queries
    if (existingCSS) {
      const existingMediaQueries = existingCSS.match(/@media[^}]*}[^}]*}/g) || [];
      existingMediaQueries.forEach((query) => {
        // Normalize whitespace for comparison
        const normalized = query.replace(/\s+/g, ' ').trim();
        existingQueries.add(normalized);
      });
    }

    let newCSS = '';
    minHeightsPairs.forEach((pair) => {
      const [breakpoint, heightValue] = pair.split(':').map((s) => s.trim());

      if (breakpoint && heightValue) {
        // Parse height value to extract number and unit
        const heightMatch = heightValue.match(/^(\d+(?:\.\d+)?)(.*)$/);
        if (heightMatch) {
          const numberPart = heightMatch[1];
          const unitPart = heightMatch[2] || 'px'; // Default to px if no unit

          const mediaQuery = `@media (max-width: ${breakpoint}px) { .product-images-container__${tileType} { min-height: ${numberPart}${unitPart} !important; } }`;

          // Normalize for comparison
          const normalizedQuery = mediaQuery.replace(/\s+/g, ' ').trim();

          // Only add if this exact media query doesn't already exist
          if (!existingQueries.has(normalizedQuery)) {
            existingQueries.add(normalizedQuery);
            newCSS += `
              @media (max-width: ${breakpoint}px) {
                .product-images-container__${tileType} {
                  min-height: ${numberPart}${unitPart} !important;
                }
              }
              `;
          }
        }
      }
    });

    // Inject the CSS
    injectCSS(newCSS, styleId);

    return styleId;
  };

  const dispatchComponentEvent = (actions, detail) => {
    const actionIds = actions.split(',');

    const actionMapping = {
      // Cart actions
      add_to_cart: 'runAddToCart',
      add_to_cart_flyout: 'runAddToCartFlyout',
      quick_add_to_cart: 'runQuickAddToCart',
      add_to_cart_open_cart: 'runAddToCartAndCartOpen',
      open_cart: 'runCartOpen',
      close_cart: 'runCartClose'
    };

    actionIds.forEach((actionId) => {
      const trimmedActionId = actionId.trim();
      const mappedAction = actionMapping[trimmedActionId];

      if (mappedAction) {
        if ((window as any).triggerAction) {
          (window as any).triggerAction(mappedAction, detail);
        }
      } else {
        window.dispatchEvent(new CustomEvent(trimmedActionId, { detail }));
      }
    });
  };

  // Load metafields on component mount instead of waiting for hover
  // useEffect(() => {
  //   // Set flag to prevent re-execution - metafields will be loaded contextually in components
  //   if (!hasRetrievedMetafields && product?.id) {
  //     sethasRetrievedMetafields(true);
  //   }
  // }, [hasRetrievedMetafields, product?.id]);

  // Update current product when prop changes
  useEffect(() => {
    setCurrentProduct(product);
  }, [product]);

  // Add event listener for product change events
  useEffect(() => {
    const handleProductChangeEvent = (event: Event) => {
      // Only handle events that originate from within this specific ProductTile
      const customEvent = event as CustomEvent;
      if (productTileRef.current && customEvent.detail?.targetProductTileId) {
        // Check if this event is meant for this specific ProductTile
        if (customEvent.detail.targetProductTileId === productTileId) {
          handleProductChange(customEvent);
        }
      }
    };

    document.addEventListener('product:change', handleProductChangeEvent);

    return () => {
      document.removeEventListener('product:change', handleProductChangeEvent);
    };
  }, [currentProduct?.handle]); // Re-run when current product handle changes

  // Component mapping for dynamic rendering
  const componentMap = {
    Group: ({ block, settings, children, ...props }) => {
      const groupSettings = block?.settings || {};
      const getSizeClasses = () => {
        let classes: string[] = [];

        // Width handling
        if (groupSettings.use_specific_width) {
          const width = groupSettings.specific_width || 100;
          if (width < 100) {
            classes.push(`w-[${width}%]`);
          } else {
            classes.push('w-full');
          }
        } else {
          const flexBasis = groupSettings.flex_basis || 100;
          if (flexBasis < 100) {
            classes.push(`basis-[${flexBasis}%]`);
          } else {
            classes.push('basis-[100%]');
          }
        }

        // Max width
        if (groupSettings.max_width > 0) {
          classes.push(`max-w-[${groupSettings.max_width}px]`);
        }

        // Block alignment
        if (groupSettings.alignment === 'center') {
          classes.push('mx-auto');
        } else if (groupSettings.alignment === 'end') {
          classes.push('ml-auto mr-0');
        }

        return classes.join(' ');
      };

      const getBackgroundStyles = () => {
        if (!groupSettings.background_enabled || !groupSettings.background_color) {
          return {};
        }

        const opacity = (groupSettings.background_opacity || 100) / 100;
        return {
          backgroundColor: groupSettings.background_color,
          opacity: opacity,
          borderRadius: `${groupSettings.background_radius || 0}px`
        };
      };

      const groupClasses = [
        'group-block',
        `color-${groupSettings.color_scheme || 'scheme-1'}`,
        getSizeClasses(),
        groupSettings.background_enabled ? `rounded-[${groupSettings.background_radius || 0}px]` : '',
        groupSettings.classes || ''
      ]
        .filter(Boolean)
        .join(' ');

      const targetId = block?.id.split('_').splice(-1)[0] || '';

      const contentClasses = [`group-blocks group-blocks-container group-blocks-container--${targetId} !relative h-full`].join(' ');

      // Animation attributes
      const animationAttrs = groupSettings.enable_animations
        ? {
            'data-aos': groupSettings.animation_type || 'slide-fade-up',
            'data-aos-duration': groupSettings.animation_duration || 800,
            'data-aos-delay': groupSettings.animation_delay || 0,
            'data-aos-trigger': groupSettings.animation_trigger || 'onscroll'
          }
        : {};

      // Collapsible attributes for Alpine.js
      const collapsibleAttrs = groupSettings.enable_collapsible
        ? {
            'x-data': `{ isCollapsed: ${groupSettings.collapsed_by_default || false} }`,
            ...(groupSettings.target_id
              ? {
                  'x-on:toggle-collapse-window': 'isCollapsed = !isCollapsed',
                  id: groupSettings.target_id
                }
              : {})
          }
        : {};

      // If this group has nested layout items, render them
      const renderNestedBlocks = () => {
        if (block?.layout && block.layout.length > 0) {
          return block.layout.map((nestedBlock, nestedIndex) =>
            renderComponent(nestedBlock, `${block.id || 'group'}-${nestedIndex}`)
          );
        }
        return children;
      };

      return (
        <div
          className={`responsive-${targetId} ${groupClasses}`}
          style={{
            ...getBackgroundStyles()
          }}
          data-block-id={block?.id}
          data-block-type='group'
          data-horizontal-align={groupSettings.content_horizontal_align}
          data-vertical-align={groupSettings.content_vertical_align}
          {...animationAttrs}
          {...(groupSettings.enable_collapsible ? collapsibleAttrs : {})}
        >
          {/* Background overlay for opacity control */}
          {groupSettings.background_enabled && groupSettings.background_color && (
            <div
              className={`absolute inset-0 pointer-events-none rounded-[${groupSettings.background_radius || 0}px]`}
              style={{
                backgroundColor: groupSettings.background_color,
                opacity: (groupSettings.background_opacity || 100) / 100
              }}
            />
          )}

          {/* Built-in Toggle Button */}
          {groupSettings.enable_collapsible && groupSettings.show_toggle_button && (
            <div
              className={`collapsible-toggle-wrapper relative z-10 flex mb-4 ${
                groupSettings.toggle_position === 'right'
                  ? 'justify-end'
                  : groupSettings.toggle_position === 'center'
                  ? 'justify-center'
                  : 'justify-start'
              }`}
            >
              <button
                onClick={() => {
                  // Dispatch component event
                  dispatchComponentEvent('GroupToggle', { action: 'toggle_click', groupId: block?.id });

                  // Handle toggle logic - this would need to be connected to Alpine.js or React state
                  console.log('Toggle collapsed state');
                }}
                className={`collapsible-toggle-btn flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                  groupSettings.toggle_style === 'primary'
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : groupSettings.toggle_style === 'secondary'
                    ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                    : 'bg-transparent border-2 border-gray-300 text-gray-700 hover:border-gray-400'
                }`}
              >
                <span className='toggle-text'>{groupSettings.toggle_text || 'Toggle Content'}</span>

                {/* Toggle Icons */}
                {groupSettings.toggle_icon_style === 'plus-minus' && (
                  <>
                    <span className='toggle-icon text-lg'>+</span>
                    {/* Note: In actual implementation, this would be conditional based on collapsed state */}
                  </>
                )}
                {groupSettings.toggle_icon_style === 'chevron' && (
                  <svg
                    className='w-4 h-4 transition-transform'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M19 9l-7 7-7-7'
                    ></path>
                  </svg>
                )}
                {groupSettings.toggle_icon_style === 'arrow' && (
                  <svg
                    className='w-4 h-4 transition-transform'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M13 7l5 5-5 5M6 12h12'
                    ></path>
                  </svg>
                )}
              </button>
            </div>
          )}

          {/* Content Container */}
          <div
            className={`groups-blocks-container ${contentClasses}`}
            style={
              groupSettings.enable_collapsible
                ? {
                    display: groupSettings.collapsed_by_default ? 'none' : 'block'
                    // Note: In actual implementation, this would be controlled by Alpine.js or React state
                  }
                : {}
            }
          >
            {renderNestedBlocks()}
          </div>
        </div>
      );
    },
    ProductImages: ({ product, productUrl, blockSettings }) => {
      // Handle min heights parsing when blockSettings.min_heights changes
      useEffect(() => {
        if (blockSettings?.min_heights) {
          parseMinHeightsString(blockSettings.min_heights, productTileId);
        }
      }, [blockSettings?.min_heights]);

      // Generate padding styles from blockSettings
      const getPaddingStyles = () => {
        const styles: React.CSSProperties = {};
        
        if (blockSettings?.padding_top !== undefined) {
          styles.paddingTop = `${blockSettings.padding_top}px`;
        }
        if (blockSettings?.padding_bottom !== undefined) {
          styles.paddingBottom = `${blockSettings.padding_bottom}px`;
        }
        if (blockSettings?.padding_left !== undefined) {
          styles.paddingLeft = `${blockSettings.padding_left}px`;
        }
        if (blockSettings?.padding_right !== undefined) {
          styles.paddingRight = `${blockSettings.padding_right}px`;
        }
        
        return styles;
      };

      return (
        <div 
          className={`product-images-container__${tileType}`}
          style={getPaddingStyles()}
        >
          <ProductImages
            productUrl={`${productUrl}` + product?.handle}
            isMobile={isMobile}
            product={product}
            settings={blockSettings}
          />
        </div>
      );
    },
    ProductQuickAdd: ({ product, showQuickAdd, blockSettings }) => {
      const { action_preset, custom_actions } = blockSettings;

      return showQuickAdd && (!isMobile || (isMobile && blockSettings.enable_quick_add_mobile)) ? (
        <QuickAdd
          product={product}
          isMobile={isMobile}
          isHovered={true}
          settings={blockSettings}
          onVariantChange={(variant, product) => {
            dispatchComponentEvent('ProductQuickAdd', {
              action: 'variant_change',
              variant,
              product
            });
          }}
          onAddToCart={(variant) => {
            dispatchComponentEvent(`${action_preset === 'custom' ? custom_actions : action_preset}`, {
              action: 'add_to_cart',
              variantId: variant.id
            });
          }}
        />
      ) : null;
    },
    ProductWishlist: ({ product, settings, enableWishlist, block, type, blockSettings }) =>
      enableWishlist ? (
        <div
          className={
            !isMobile && blockSettings.desktop_display === 'on_hover' ? `opacity-0 group-hover:opacity-100` : ''
          }
        >
          <WishlistButton
            type={'icon'}
            settings={settings}
            product={product}
            iconSize={isMobile ? block?.layout[0]?.settings?.icon_size_mobile || 16 : block?.layout[0]?.settings?.icon_size || 24}
            blockSettings={blockSettings}
          />
        </div>
      ) : null,
    ProductRatings: ({ product, showRating, blockSettings }) =>
      showRating ? (
        <ProductRatings
          product={product}
          rating_system={blockSettings?.rating_system}
        />
      ) : null,
    ProductTitle: ({ product, blockSettings }) => (
      <ProductTitle
        product={product}
        isMobile={isMobile}
        settings={blockSettings}
        globals={globals}
        onTitleClick={(event) => dispatchComponentEvent('ProductTitle', event)}
      />
    ),
    ProductVendor: ({ product, block, blockSettings }) => (
      <ProductVendor
        product={product}
        block={block}
        isMobile={isMobile}
        settings={blockSettings}
        globals={globals}
      />
    ),
    ProductPrice: ({ product, showPrice, blockSettings }) => (
      showPrice && product ? (
        <ProductPrice
          product={product}
          isMobile={isMobile}
          settings={blockSettings}
          globals={globals}
        />
      ) : null
    ),
    ProductTags: ({ product, settings, showTags, blockSettings, isClosestMatch }) => {
      return showTags ? (
        <ProductTags
          product={product}
          globals={settings.globals}
          isMobile={isMobile}
          settings={blockSettings}
          isClosestMatch={isClosestMatch}
        />
      ) : null;
    },
    ProductSwatches: ({ relatedProducts, colourName, blockSettings, product, settings }) => {
      // State to store processed swatches
      const [processedSwatches, setProcessedSwatches] = useState<SwatchItem[]>([]);
      const [isLoadingSwatches, setIsLoadingSwatches] = useState(false);

      // Generate unique component ID for debugging
      const componentId = useRef(
        `ProductSwatches-${product?.id || 'unknown'}-${Math.random().toString(36).substr(2, 9)}`
      ).current;

      // Process swatches when component mounts or dependencies change
      useEffect(() => {
        // Don't process swatches if product is not fully loaded yet
        if (!product?.id) {
          return;
        }

        // For metafields source type, wait for products to be fully loaded with metafields
        if (blockSettings?.source_type === 'metafields' || blockSettings?.source_type === 'metaobjects') {
          // Check if this looks like a partially loaded product (missing expected metafields structure)
          // If product.metafields is undefined/null, it might still be loading
          if (product.metafields === undefined || product.metafields === null) {
            return;
          }
        }

        // Process swatches based on block settings source type
        const processSwatchData = async (): Promise<SwatchItem[]> => {
          const sourceType = blockSettings?.source_type || 'variants';
          const sourceField = blockSettings?.source_field || '';
          let swatches: SwatchItem[] = [];

          switch (sourceType) {
            case 'variants':
              // Use product variants as swatches
              if (product?.variants) {
                swatches = product.variants.map((variant: any) => ({
                  id: variant.id,
                  variantId: variant.id,
                  title: variant.title,
                  name: variant.title,
                  url: `${product.url || `/products/${product.handle}`}?variant=${variant.id}`,
                  image: variant.image?.src || variant.featured_image?.src,
                  color: null, // Will be determined by color mapping
                  isSelected: product.selected_variant?.id === variant.id
                }));

                // Extract color information from variant options
                swatches.forEach((swatch: SwatchItem) => {
                  const variant = product.variants.find((v: any) => v.id === swatch.variantId);
                  if (variant?.options) {
                    for (const option of variant.options) {
                      const optionLower = option.toLowerCase();
                      if (optionLower.includes('color') || optionLower.includes('colour')) {
                        swatch.name = option;
                        // Apply color mapping if available
                        if (blockSettings?.colour_mapping) {
                          const colorMapping = blockSettings.colour_mapping.split(',');
                          for (const colorPair of colorMapping) {
                            const [colorName, colorValue] = colorPair.split(':');
                            if (colorName && colorValue && optionLower.includes(colorName.trim().toLowerCase())) {
                              swatch.color = colorValue.trim();
                              break;
                            }
                          }
                        }
                        break;
                      }
                    }
                  }
                });
              }
              break;

            case 'metafields':
              // Use product metafields - support for existing bottle_siblings logic
              if (relatedProducts && relatedProducts.length > 0) {
                swatches = relatedProducts;
              } else if (product?.id && sourceField) {
                try {
                  // Parse sourceField to extract namespace and key
                  // Expected format: "namespace.key" or just "key" (defaults to "custom" namespace)
                  const fieldParts = sourceField.includes('.') ? sourceField.split('.') : ['custom', sourceField];
                  const namespace = fieldParts[0];
                  const key = fieldParts[1] || fieldParts[0];

                  let metafieldValue = null;
                  let shouldSkipApiCall = false;

                  // Simple, direct check for existing metafield
                  if (product.metafields) {
                    // Check if metafields is array or object and look for our specific field
                    if (Array.isArray(product.metafields)) {
                      const existingField = product.metafields.find(
                        (m: any) => (m.namespace === namespace && m.key === key) || m.key === sourceField
                      );
                      if (existingField) {
                        metafieldValue = existingField.value;
                        shouldSkipApiCall = true;
                      }
                    } else {
                      // Object format - check multiple possible keys
                      metafieldValue =
                        product.metafields[sourceField] ||
                        product.metafields[key] ||
                        product.metafields[`${namespace}.${key}`];
                      if (metafieldValue !== undefined) {
                        shouldSkipApiCall = true;
                      }
                    }
                  }

                  // Only make API call if we don't have the metafield
                  if (!shouldSkipApiCall) {
                    // Create custom metafield identifiers
                    const metafieldIdentifiers = [{ namespace, key }];

                    // Fetch metafields with custom identifiers
                    const productMetafields = await shopifyHelper.getMetafieldsForProduct(
                      [`gid://shopify/Product/${product.id}`],
                      metafieldIdentifiers as any
                    );

                    if (productMetafields && productMetafields[0]?.metafields && Array.isArray(productMetafields[0].metafields)) {
                      // Update product metafields
                      product.metafields = product.metafields || {};
                      productMetafields[0].metafields.forEach((metafield) => {
                        product.metafields[metafield.key] = metafield.value;
                      });

                      // Get the specific metafield value
                      metafieldValue = productMetafields[0].metafields.find((m) => m.key === key)?.value;
                    }
                  }

                  // Process the metafield value (whether from existing data or API)
                  if (metafieldValue) {
                    if (blockSettings?.metafield_format === 'json') {
                      // Handle JSON metafields
                      try {
                        const jsonData =
                          typeof metafieldValue === 'string' ? JSON.parse(metafieldValue) : metafieldValue;
                        swatches = Array.isArray(jsonData) ? jsonData : [jsonData];
                      } catch (e) {
                        console.warn('Failed to parse JSON metafield:', e);
                      }
                    } else {
                      // Handle text/CSV metafields
                      const values =
                        typeof metafieldValue === 'string'
                          ? (metafieldValue as string).split(',')
                          : [String(metafieldValue)];
                      swatches = values.map((value: string) => ({
                        name: value.trim(),
                        title: value.trim(),
                        url: product.url || `/products/${product.handle}`
                      }));
                    }
                  }
                } catch (error) {
                  console.error('Error processing metafields:', error);
                }
              }
              break;

            case 'metaobjects':
              if (relatedProducts && relatedProducts.length > 0) {
                swatches = relatedProducts;
              } else if (product?.id && sourceField) {
                try {
                  // Parse sourceField to extract namespace and key
                  // Expected format: "namespace.key" or just "key" (defaults to "custom" namespace)
                  const fieldParts = sourceField.includes('.') ? sourceField.split('.') : ['custom', sourceField];
                  const namespace = fieldParts[0];
                  const key = fieldParts[1] || fieldParts[0];

                  let metafieldValue = null;
                  let shouldSkipApiCall = false;

                  // Simple, direct check for existing metafield
                  if (product.metafields) {
                    // Check if metafields is array or object and look for our specific field
                    if (Array.isArray(product.metafields)) {
                      const metaobjectField = product.metafields.find(
                        (m: any) =>
                          (m.namespace === namespace && m.key === key) ||
                          m.key === sourceField ||
                          (m.reference && m.reference.field && m.reference.field.key === 'products')
                      );

                      // If we found a metafield with reference to products
                      if (metaobjectField && metaobjectField.reference && metaobjectField.reference.field) {
                        const productReferences = metaobjectField.reference.field.references?.edges || [];
                        if (productReferences.length > 0) {
                          // We have product references - map them to swatches
                          swatches = productReferences.map((edge: any) => {
                            const node = edge.node;
                            return {
                              id: node.id,
                              title: node.colourName?.value || node.title,
                              handle: node.handle.replace('/products/', ''),
                              url: node.handle.includes('/products/') ? node.handle : `/products/${node.handle}`,
                              color: node.colourName?.value || node.title,
                              colour: node.colourHex?.value || null
                            };
                          });
                          shouldSkipApiCall = true;
                        }
                      } else {
                        // Regular metafield check as before
                        const existingField = product.metafields.find(
                          (m: any) => (m.namespace === namespace && m.key === key) || m.key === sourceField
                        );
                        if (existingField) {
                          metafieldValue = existingField.value;
                          shouldSkipApiCall = true;
                        }
                      }
                    } else {
                      // Object format - check multiple possible keys
                      metafieldValue =
                        product.metafields[sourceField] ||
                        product.metafields[key] ||
                        product.metafields[`${namespace}.${key}`];
                      if (metafieldValue !== undefined) {
                        shouldSkipApiCall = true;
                      }
                    }
                  }

                  // Only make API call if we don't have the metafield
                  if (!shouldSkipApiCall) {
                    // Create custom metafield identifiers
                    const metafieldIdentifiers = [{ namespace, key }];

                    // Fetch metafields with custom identifiers
                    const productMetafields = await shopifyHelper.getMetafieldsForProduct(
                      [`gid://shopify/Product/${product.id}`],
                      metafieldIdentifiers as any
                    );

                    if (productMetafields && productMetafields[0]?.metafields && Array.isArray(productMetafields[0].metafields)) {
                      // Update product metafields
                      product.metafields = product.metafields || {};
                      productMetafields[0].metafields.forEach((metafield) => {
                        product.metafields[metafield.key] = metafield.value;
                      });

                      // Get the specific metafield value
                      metafieldValue = productMetafields[0].metafields.find((m) => m.key === key)?.value;
                    }
                  } else {
                  }

                  // Process the metafield value (whether from existing data or API) if we haven't already created swatches
                  if (metafieldValue && swatches.length === 0) {
                    if (blockSettings?.metafield_format === 'json') {
                      // Handle JSON metafields
                      try {
                        const jsonData =
                          typeof metafieldValue === 'string' ? JSON.parse(metafieldValue) : metafieldValue;
                        swatches = Array.isArray(jsonData) ? jsonData : [jsonData];
                      } catch (e) {
                        console.warn('Failed to parse JSON metafield:', e);
                      }
                    } else {
                      // Handle text/CSV metafields
                      const values =
                        typeof metafieldValue === 'string'
                          ? (metafieldValue as string).split(',')
                          : [String(metafieldValue)];
                      swatches = values.map((value: string) => ({
                        name: value.trim(),
                        title: value.trim(),
                        url: product.url || `/products/${product.handle}`
                      }));
                    }
                  }
                } catch (error) {
                  console.error('Error processing metaobjects:', error);
                }
              }
              break;

            case 'tags':
              // Use product tags with prefix filtering
              if (product?.tags && sourceField) {
                const filteredTags = product.tags.filter((tag: string) =>
                  tag.toLowerCase().includes(sourceField.toLowerCase())
                );
                swatches = filteredTags.map((tag: string) => {
                  const cleanTag = tag.replace(new RegExp(`^${sourceField}:?`, 'i'), '').trim();
                  return {
                    name: cleanTag,
                    title: cleanTag,
                    url: product.url || `/products/${product.handle}`
                  };
                });
              }
              break;

            case 'related_products':
              // Use related products based on type, vendor, or tag
              // This would require additional product data that's not immediately available
              // in the current context, so we'll use relatedProducts if available
              if (relatedProducts && relatedProducts.length > 0) {
                swatches = relatedProducts;
              }
              break;

            default:
              // Fallback to relatedProducts from metafields processing
              swatches = relatedProducts || [];
          }

          // Apply color mapping to any swatches that don't have colors
          if (blockSettings?.colour_mapping && swatches.length > 0) {
            const colorMapping = blockSettings.colour_mapping.split(',');
            swatches.forEach((swatch: SwatchItem) => {
              if (!swatch.color && swatch.name) {
                const swatchNameLower = swatch.name.toLowerCase();
                for (const colorPair of colorMapping) {
                  const [colorName, colorValue] = colorPair.split(':');
                  if (colorName && colorValue && swatchNameLower.includes(colorName.trim().toLowerCase())) {
                    swatch.color = colorValue.trim();
                    break;
                  }
                }
              }
            });
          }

          // Limit swatches based on max_swatches setting
          const maxSwatches = blockSettings?.max_swatches || 10;
          return swatches.slice(0, maxSwatches);
        };

        const loadSwatches = async () => {
          if (isLoadingSwatches) return; // Prevent multiple simultaneous loads

          setIsLoadingSwatches(true);
          try {
            const swatches = await processSwatchData();
            setProcessedSwatches(swatches);
          } catch (error) {
            console.error('Error processing swatches:', error);
            setProcessedSwatches([]);
          } finally {
            setIsLoadingSwatches(false);
          }
        };

        loadSwatches();
      }, [
        blockSettings?.source_type,
        blockSettings?.source_field,
        blockSettings?.metafield_format,
        blockSettings?.colour_mapping,
        blockSettings?.max_swatches,
        product?.id,
        product?.handle,
        product?.url,
        // Only re-run when metafields actually change, not on every render
        product?.metafields ? JSON.stringify(product.metafields) : 'no-metafields',
        JSON.stringify(relatedProducts) // Stringify to handle array comparison
      ]);

      // Determine current color/swatch
      const getCurrentColour = () => {
        // Check if product has siblings metafield and loop through to find matching handle
        let siblingsData = null;

        // Only check metafields if they exist and are not empty
        if (
          product?.metafields &&
          ((Array.isArray(product.metafields) && product.metafields.length > 0) ||
            (!Array.isArray(product.metafields) && Object.keys(product.metafields).length > 0))
        ) {
          // If metafields is an array (from GraphQL), search for siblings metafield
          if (Array.isArray(product.metafields)) {
            const siblingsMetafield = product.metafields.find(
              (m: any) =>
                (m.namespace === 'info' && m.key === 'siblings') ||
                (m.namespace === 'custom' && m.key === 'siblings') ||
                m.key === 'siblings'
            );
            if (siblingsMetafield?.reference?.field?.references?.edges) {
              const productSiblings = siblingsMetafield.reference.field.references?.edges

              siblingsData = productSiblings.map(({ node }) => {
                const handle = node.handle.replace(/^\/+/, ""); // remove leading slash

                return {
                  "@type": "Product",
                  "name": node.colourName.value,
                  "url": handle,
                  "color": node.colourName.value,
                  "colour": node.colourHex.value,
                  "handle": handle
                };
              });
            } else {
              siblingsData = siblingsMetafield?.value;
            }
          } else {
            siblingsData =
              product.metafields.siblings ||
              product.metafields['info.siblings'] ||
              product.metafields['custom.siblings'];
          }
        }

        if (typeof siblingsData === 'string') {
          try {
            siblingsData = siblingsData ? JSON.parse(siblingsData) : null;
          } catch (error) {
            console.error('Error parsing siblings data:', error);
            siblingsData = null;
          }
        }

        if (siblingsData) {
          // Find the sibling that matches current product handle
          if (Array.isArray(siblingsData)) {
            const matchingSibling = (siblingsData as any[]).find((sibling: any) => sibling.handle === product.handle);

            if (matchingSibling) {
              return matchingSibling?.color || matchingSibling?.name?.includes('-')
                ? matchingSibling?.name?.substring(matchingSibling?.name?.lastIndexOf('-') + 1).trim()
                : matchingSibling?.name;
            }
          }
        }

        if (colourName) return colourName;
        if (product?.selected_variant?.title) return product.selected_variant.title;
        if (blockSettings?.source_type === 'variants' && product?.variants?.[0]) {
          return product.variants[0].title;
        }
        return null;
      };

      const currentColour = getCurrentColour();

      // Determine swatch type
      const getSwatchType = (): 'image' | 'colour' | 'text' => {
        if (processedSwatches.some((s: SwatchItem) => s.image)) return 'image';
        if (processedSwatches.some((s: SwatchItem) => s.colour)) return 'colour';
        return 'text';
      };

      const swatchType = getSwatchType();

      // Only render if we have swatches and not loading
      if (isLoadingSwatches || processedSwatches.length === 0) return null;

      // Get swatch count text
      const swatchCountText =
        processedSwatches.length > 1
          ? `${blockSettings.showPlus ? '+' : ''}${processedSwatches.length} ${
              blockSettings?.swatch_count_text || 'options'
            }`
          : `${blockSettings.showPlus ? '+' : ''}${processedSwatches.length} ${
              blockSettings?.swatch_count_text_singular || 'option'
            }`;

      // Determine if options text should be shown
      const shouldShowOptionsText =
        (isMobile && blockSettings?.enable_options_text_mobile && blockSettings?.enable_options_text_swatches) ||
        (!isMobile && blockSettings?.enable_options_text && blockSettings?.enable_options_text_swatches);

      // Get options text style
      const optionsTextStyle = isMobile ? blockSettings?.options_text_style_mobile : blockSettings?.options_text_style;

      return (
        <div className='product-swatches-container'>
          {/* Desktop: Show text placeholder until hovered (only if hover is enabled and not showing text beside swatches) */}
          {!isMobile &&
            blockSettings?.enable_hover_desktop &&
            blockSettings?.enable_options_text &&
            blockSettings?.enable_options_text_hover &&
            !blockSettings?.enable_options_text_swatches && (
              <div
                className='group-hover:hidden'
                style={{
                  marginTop: `${blockSettings?.margin_top || 8}px`,
                  marginBottom: `${blockSettings?.margin_bottom || 0}px`,
                  marginLeft: `${blockSettings?.margin_left || 0}px`,
                  marginRight: `${blockSettings?.margin_right || 0}px`
                }}
              >
                <div className={blockSettings?.options_text_style}>{swatchCountText}</div>
              </div>
            )}

          <div
            className={clsx({
              'hidden group-hover:block': !isMobile && blockSettings?.enable_hover_desktop,
              block: isMobile || (!isMobile && !blockSettings?.enable_hover_desktop)
            })}
          >
            <ProductSwatches
              isMobile={isMobile}
              swatches={processedSwatches}
              currentColour={currentColour}
              swatchType={swatchType}
              showName={blockSettings?.show_current_name !== false}
              title={blockSettings?.title}
              product={product}
              selectedVariantId={product?.selected_variant?.id}
              settings={blockSettings || {}}
              showOptionsText={shouldShowOptionsText}
              optionsText={swatchCountText}
              optionsTextStyle={optionsTextStyle || ''}
              onSwatchClick={(swatch) => {
                // Dispatch component event
                dispatchComponentEvent('ProductSwatches', { swatch, action: 'swatch_click' });

                // Handle swatch click based on action type
                const actionType = blockSettings?.action_type || 'select_variant';

                // Only handle redirects for explicit redirect_to_product action type
                if (actionType === 'redirect_to_product' && swatch.url) {
                  // Navigate to product/variant page
                  window.location.href = swatch.url;
                }
                // For select_variant, let the ProductSwatches component handle it via events
                // No other logic needed here to prevent redirects
              }}
            />
          </div>
        </div>
      );
    },
    ProductSellingGroups: ({ product, blockSettings }) =>
      product?.selling_plan_groups?.length > 0 ? (
        <ProductSellingGroups
          product={product}
          showAddToCart={true}
          isInTile
          settings={blockSettings}
        />
      ) : null
  };

  // Render component based on layout configuration
  const renderComponent = (layoutItem, index) => {
    const Component = componentMap[layoutItem.name];

    if (!Component) {
      console.warn(`Component ${layoutItem.name} not found in componentMap`);
      return null;
    }

    // Prepare props for the component - remove isHovered
    const componentProps = {
      product: currentProduct, // Use currentProduct instead of product
      settings,
      enableWishlist,
      showPrice: showPrice ?? settings?.show_price ?? settings?.globals?.show_price,
      showTags: settings?.show_tags ?? showTags,
      showQuickAdd:
        settings?.show_quickadd ?? settings?.globals?.product_tiles?.quickview_add_to_cart_enabled ?? showQuickAdd,
      showRating: false,
      relatedProducts,
      colourName,
      productUrl: settings?.collection ? `/collections/${settings.collection?.handle}/products/` : `/products/`,
      type: type || 'plp',
      block: layoutItem, // Pass the layout item as block for Group components
      blockSettings: layoutItem.settings, // Merge any settings from the layout item
      isLoadingProduct, // Pass loading state to components that might need it
      isClosestMatch // Pass closest match flag to components that might need it
    };

    return (
      <Component
        key={index}
        {...componentProps}
      />
    );
  };

  // Fallback to original structure if no layout is defined
  if (!layout || layout.length === 0) {
  }

  return (
    <div
      ref={productTileRef}
      id={productTileId}
      className={`theme__product-card product-tile-${productTileId} group relative ${isClosestMatch ? 'closest-match-tile' : ''}`}
      data-product-id={currentProduct?.id}
    >
      {/* Loading overlay when switching products */}
      {isLoadingProduct && (
        <div className='absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900'></div>
        </div>
      )}

      <div className='productcard__content'>
        {layout.map((layoutItem, index) => renderComponent(layoutItem, index))}
      </div>
    </div>
  );
};

export default ProductTile;
